/* global globalThis */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import AdminPage from "../component/pages/Falcon360/admin/AdminPage";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Dashboard from "../component/pages/Falcon360/dashboard/dashboard.js";
import Header from "../component/header/Header.js";
import Sidebar from "../component/sidebar/Sidebar.js";
import CreateTable from "../component/pages/Falcon360/targetMapping/CreateTable.js";
import Footer from "../component/footer/Footer.js";
import AuditLogsTable from "../component/common/audit/AuditLogs.js";
import ImportData from "../component/pages/Falcon360/upload/ImportData.js";
import ErrorPage from "../component/common/ErrorPage.js";
import DataSource from "../component/pages/Falcon360/dataSource/DataSource.js";
import ChatInterface from "../ai-agent/ui/ChatInterface.js";
import ChatModal from "../ai-agent/ui/ChatModal.js";
import DatabaseConnectionsTable from "../component/pages/Falcon360/dataSource/DataConnections.js";
import UploadedObjectsTable from "../component/pages/Falcon360/upload/UploadedObjectsTable.js";
import HistoryData from "../component/pages/Falcon360/targetMapping/HistoryData.js";
import { LoaderCircle } from "lucide-react";
import axiosClient, { setAutoLogoutReset } from "../api-client/AxiosClient.js";
import FalconAIChatbot from "../ai-agent/ui/FalconAIChatbot.js";
import TaskManagement from "../component/pages/Todo/TaskManagement.js";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import useAutoLogout from "../hooks/useAutoLogout";
import AutoLogoutWarning from "../component/common/AutoLogoutWarning/AutoLogoutWarning";
import DataLineageContainer from "../component/pages/Falcon360/DataLineage/DataLineageContainer.js";
import UnderDevelopment from "../component/common/UnderDevelopment.js";
import MappingComparison from "../component/pages/Falcon360/upload/Mappingcomparison.js";
import CreateWorkItem from "../component/pages/Todo/CreateWorkItem.js";

const ProtectedRoute = ({ canView, children }) => {
  return canView ? children : <Navigate to="/dashboard" replace />;
};

ProtectedRoute.propTypes = {
  canView: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

const Layout = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { instance } = useMsal();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [isRolesLoaded, setIsRolesLoaded] = useState(false);
  const [isUserGuideLoaded, setIsUserGuideLoaded] = useState(false);
  const [run, setRun] = useState(false);

  // Auto-logout handlers
  const handleAutoLogout = useCallback(() => {
    instance.logout().catch(() => {
      // Error handled by logout mechanism
    });
    toast.warning(
      "Your session has expired due to inactivity. Please login again.",
    );
  }, [instance]);

  const handleManualLogout = useCallback(() => {
    instance.logout().catch(() => {
      // Error handled by logout mechanism
    });
  }, [instance]);

  // Use auto-logout hook (15 minutes inactivity, 30 seconds warning)
  const { showWarning, remainingTime, resetTimer } = useAutoLogout(
    handleAutoLogout,
    120 * 60 * 1000, // 15 minutes
    30 * 1000, // 30 seconds warning before logout
  );

  // Register resetTimer with AxiosClient for API call tracking
  useEffect(() => {
    setAutoLogoutReset(resetTimer);
    return () => {
      setAutoLogoutReset(null);
    };
  }, [resetTimer]);

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  const isAgentRoute = location.pathname === "/ai-agent";
  const isViewDataRoute = location.pathname === "/view-data";
  const isComparisonRoute = location.pathname === "/upload/mapping-comparison";
  const isHistoryPage = location.pathname.includes("/history-data");

  const fetchisUserGuideViewed = async () => {
    const response = await axiosClient.get("api/User/GuideViewStatus");
    setRun(response.data || false);
    setIsUserGuideLoaded(true);
  };

  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setIsRolesLoaded(true);
    };

    globalThis.addEventListener("permissionsUpdated", handlePermissionsUpdated);

    // Removed cached sessionStorage check here.
    // The UI must wait for Header to determine the correct config and trigger
    // customStorageChange, which then calls fetchPermissions and unlocks the layout.

    return () => {
      globalThis.removeEventListener(
        "permissionsUpdated",
        handlePermissionsUpdated,
      );
    };
  }, []);

  useEffect(() => {
    const fetchUserGuide = async () => {
      try {
        await fetchisUserGuideViewed();
      } catch {
        // Mark as loaded even if it fails
        setIsUserGuideLoaded(true);
      }
    };

    fetchUserGuide();
  }, []);

  const getPermissions = () => {
    try {
      return JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}");
    } catch {
      return {};
    }
  };
  const [permissions, setPermissions] = useState(getPermissions());

  useEffect(() => {
    const handlePermUpdate = () => setPermissions(getPermissions());
    globalThis.addEventListener("permissionsUpdated", handlePermUpdate);
    return () =>
      globalThis.removeEventListener("permissionsUpdated", handlePermUpdate);
  }, []);

  const {
    DataSource: DataSourcePerm,
    Upload: UploadPerm,
    STTMapping: STTMappingPerm,
    Administration: AdministrationPerm,
    ToDo: ToDoPerm,
    AIAsst: AIAsstPerm,
  } = permissions;

  const closeChat = () => setChatOpen(false);
  const toggleMinimize = () => setChatMinimized((prev) => !prev);

  // Helper function to calculate margin left value
  const getMarginLeftValue = () => {
    if (isAgentRoute) {
      return "0px";
    }
    return isOpen ? "0px" : "80px";
  };

  // Calculate content width based on route and sidebar state
  const getContentStyle = () => {
    const marginLeftValue = getMarginLeftValue();
    const baseStyle = {
      marginLeft: marginLeftValue,
      marginTop: "80px", // Add margin-top to account for fixed header
      padding: isAgentRoute ? "0px" : "10px",
      width: "100%",
      transition: "all 0.3s ease-in-out",
    };

    if (isHistoryPage) {
      return {
        ...baseStyle,
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    // Special handling for view-data route (CreateTable component)
    if (isViewDataRoute) {
      return {
        ...baseStyle,
        maxWidth: isOpen ? "calc(100vw)" : "calc(100vw - 50px)",
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    if (isComparisonRoute) {
      return {
        ...baseStyle,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 80px)",
        padding: "0px",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    return baseStyle;
  };

  return (
    <>
      {isUserGuideLoaded ? (
        <div
          className="app-wrapper"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Header
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            setRun={setRun}
            run={run}
          />

          <div
            className={`app-container ${isAgentRoute ? "h-screen" : ""}`}
            style={{ display: "flex", flex: 1 }}
          >
            {!isAgentRoute && <Sidebar isOpen={isOpen} />}

            <div className="content" style={getContentStyle()}>
              {isRolesLoaded ? (
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route
                    path="/data-sources"
                    element={
                      <ProtectedRoute canView={DataSourcePerm?.canView}>
                        <DatabaseConnectionsTable />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/data-sources/add-data-source"
                    element={
                      <ProtectedRoute canView={DataSourcePerm?.canView}>
                        <DataSource />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/upload"
                    element={
                      <ProtectedRoute canView={UploadPerm?.canView}>
                        <UploadedObjectsTable />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/upload/add-upload"
                    element={
                      <ProtectedRoute canView={UploadPerm?.canView}>
                        <ImportData />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/upload/mapping-comparison"
                    element={
                      <ProtectedRoute canView={UploadPerm?.canView}>
                        <MappingComparison />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/view-data"
                    element={
                      <ProtectedRoute canView={STTMappingPerm?.canView}>
                        <CreateTable />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/history-data/:id"
                    element={
                      <ProtectedRoute canView={STTMappingPerm?.canView}>
                        <HistoryData />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/falcon-ai"
                    element={
                      <ProtectedRoute canView={AIAsstPerm?.canView}>
                        <FalconAIChatbot />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/task"
                    element={
                      <ProtectedRoute canView={ToDoPerm?.canView}>
                        <TaskManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/task/create"
                    element={
                      <ProtectedRoute canView={ToDoPerm?.canView}>
                        <CreateWorkItem />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/task/edit/:taskId"
                    element={
                      <ProtectedRoute canView={ToDoPerm?.canView}>
                        <CreateWorkItem />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin-interface"
                    element={
                      <ProtectedRoute canView={AdministrationPerm?.canView}>
                        <AdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/logs" element={<AuditLogsTable />} />
                  <Route path="/ai-agent" element={<ChatInterface />} />
                  <Route path="/error" element={<ErrorPage />} />
                  <Route path="/erd" element={<UnderDevelopment />} />
                  <Route path="/erd1" element={<DataLineageContainer />} />
                  <Route
                    path="*"
                    element={<Navigate to="/dashboard" replace />}
                  />
                </Routes>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <LoaderCircle
                    className="animate-spin text-blue-900"
                    size={50}
                  />
                </div>
              )}
            </div>
            {/* Chat Modal */}
            <ChatModal
              open={chatOpen}
              minimized={chatMinimized}
              onClose={closeChat}
              onMinimize={toggleMinimize}
            >
              <ChatInterface />
            </ChatModal>
          </div>

          <Footer />
        </div>
      ) : (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
          <LoaderCircle className="animate-spin text-blue-900" size={50} />
        </div>
      )}
      {/* Auto-logout Warning Modal */}
      <AutoLogoutWarning
        show={showWarning}
        remainingTime={remainingTime}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleManualLogout}
      />
    </>
  );
};

Layout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default Layout;
